import React, { useMemo, useState } from "react";
import {
  Plus, Trash2, Pencil, Package, FileEdit, Archive, Play, MoreHorizontal, Check,
} from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { EmptyState } from "@/components/Common";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const empty = {
  name: "", code: "", jira_tag: "", pm_name: "", pm_email: "",
  department: "", country: "India", description: "", objectives: "", status: "draft",
};

const STATUS_STYLE = {
  draft:    "bg-muted text-muted-foreground border-border",
  active:   "bg-[#d9f2e5] text-[#25604d] border-[#a5dbc0]",
  archived: "bg-[#fbe0d9] text-[#a23a2a] border-[#f0b8a8]",
};
const STATUS_LABEL = { draft: "Draft", active: "Active", archived: "Archived" };

const FILTERS = ["all", "active", "draft", "archived"];

export default function AdminPrograms({ refs }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [confirmDel, setConfirmDel] = useState(null);
  const [filter, setFilter] = useState("all");

  const programs = refs.programs || [];
  const counts = useMemo(() => {
    const c = { all: programs.length, active: 0, draft: 0, archived: 0 };
    for (const p of programs) c[p.status || "active"] = (c[p.status || "active"] || 0) + 1;
    return c;
  }, [programs]);

  const filtered = useMemo(() => {
    if (filter === "all") return programs;
    return programs.filter((p) => (p.status || "active") === filter);
  }, [programs, filter]);

  const openCreate = () => { setEditing(null); setForm({ ...empty }); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...empty, ...p, status: p.status || "active" }); setOpen(true); };

  const persist = async (payload) => {
    if (editing) await api.put(`/programs/${editing.id}`, payload);
    else await api.post("/programs", payload);
  };

  const save = async (statusOverride) => {
    if (!form.name.trim()) { toast.error("Program name is required"); return; }
    const payload = { ...form, status: statusOverride || form.status || "active" };
    try {
      await persist(payload);
      toast.success(editing ? "Program updated" : `Program created${payload.status === "draft" ? " as draft" : ""}`);
      setOpen(false);
      refs.reload();
    } catch { toast.error("Save failed"); }
  };

  const setStatus = async (p, status) => {
    try {
      await api.patch(`/programs/${p.id}/status`, { status });
      toast.success(`Marked ${STATUS_LABEL[status]}`);
      refs.reload();
    } catch { toast.error("Update failed"); }
  };

  const remove = async (p) => {
    try {
      await api.delete(`/programs/${p.id}`);
      toast.success("Program deleted");
      setConfirmDel(null);
      refs.reload();
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="flex gap-1.5">
          {FILTERS.map((f) => (
            <button key={f} onClick={() => setFilter(f)} data-testid={`programs-filter-${f}`}
              className={`text-xs font-medium rounded-full px-3.5 py-1.5 border transition-all capitalize ${
                filter === f
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-card text-foreground/70 border-border hover:border-primary/40"
              }`}>
              {f} ({counts[f] ?? 0})
            </button>
          ))}
        </div>
        <div className="ml-auto flex items-center gap-3">
          <p className="text-[11px] font-mono uppercase tracking-[0.22em] text-muted-foreground">
            {filtered.length} shown
          </p>
          <Button onClick={openCreate} data-testid="admin-create-program" className="rounded-full">
            <Plus className="w-4 h-4 mr-1.5" /> New Program
          </Button>
        </div>
      </div>

      {filtered.length === 0 ? (
        <EmptyState icon={Package} title="No programs here yet" hint="Create a new program — save as draft to prepare it, activate when ready to onboard users." />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 stagger" data-testid="admin-programs-grid">
          {filtered.map((p) => {
            const st = p.status || "active";
            return (
              <div key={p.id} className="kp-card kp-card-lift p-5 flex flex-col" data-testid={`program-card-${p.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="w-11 h-11 rounded-xl bg-secondary/60 flex items-center justify-center">
                    <Package className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex items-center gap-1">
                    <span className={`text-[10px] font-mono uppercase tracking-wider rounded-full border px-2 py-0.5 ${STATUS_STYLE[st]}`} data-testid={`program-status-${p.id}`}>
                      {STATUS_LABEL[st]}
                    </span>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button size="icon" variant="ghost" className="h-7 w-7" data-testid={`program-menu-${p.id}`}>
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-48">
                        <DropdownMenuItem onClick={() => openEdit(p)} data-testid={`edit-program-${p.id}`}>
                          <Pencil className="w-4 h-4 mr-2" /> Edit
                        </DropdownMenuItem>
                        {st !== "active" && (
                          <DropdownMenuItem onClick={() => setStatus(p, "active")} data-testid={`activate-program-${p.id}`}>
                            <Play className="w-4 h-4 mr-2 text-[#25604d]" /> Activate
                          </DropdownMenuItem>
                        )}
                        {st !== "draft" && (
                          <DropdownMenuItem onClick={() => setStatus(p, "draft")} data-testid={`draft-program-${p.id}`}>
                            <FileEdit className="w-4 h-4 mr-2" /> Move to Draft
                          </DropdownMenuItem>
                        )}
                        {st !== "archived" && (
                          <DropdownMenuItem onClick={() => setStatus(p, "archived")} data-testid={`archive-program-${p.id}`}>
                            <Archive className="w-4 h-4 mr-2" /> Archive
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => setConfirmDel(p)}
                          data-testid={`delete-program-${p.id}`}
                          className="text-destructive focus:text-destructive">
                          <Trash2 className="w-4 h-4 mr-2" /> Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
                <h3 className="font-display text-xl leading-tight text-foreground">{p.name}</h3>
                {p.jira_tag && <p className="text-[11px] font-mono uppercase tracking-wider text-primary mt-1">{p.jira_tag}</p>}
                <p className="text-sm text-muted-foreground mt-2 line-clamp-2 flex-1">{p.description || <span className="italic opacity-60">No description</span>}</p>
                <div className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground mt-4 pt-3 border-t border-border flex items-center gap-2 flex-wrap">
                  {p.pm_name && <span>PM · {p.pm_name}</span>}
                  {p.department && <><span className="opacity-40">/</span><span>{p.department}</span></>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create / Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{editing ? "Edit Program" : "New Program"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Name *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-1.5 bg-card" data-testid="program-form-name" placeholder="e.g. Order Fulfilment" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Code</Label>
                <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })}
                  className="mt-1.5 bg-card" placeholder="e.g. OF" />
              </div>
              <div>
                <Label>Jira Tag</Label>
                <Input value={form.jira_tag} onChange={(e) => setForm({ ...form, jira_tag: e.target.value })}
                  className="mt-1.5 bg-card font-mono" placeholder="PROJ_OF" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>PM Name</Label><Input value={form.pm_name} onChange={(e) => setForm({ ...form, pm_name: e.target.value })} className="mt-1.5 bg-card" /></div>
              <div><Label>PM Email</Label><Input value={form.pm_email} onChange={(e) => setForm({ ...form, pm_email: e.target.value })} className="mt-1.5 bg-card" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Department</Label><Input value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} className="mt-1.5 bg-card" /></div>
              <div><Label>Country</Label><Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="mt-1.5 bg-card" /></div>
            </div>
            <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5 bg-card" rows={2} placeholder="One-line summary shown across the portal." /></div>
            <div><Label>Objectives</Label><Textarea value={form.objectives} onChange={(e) => setForm({ ...form, objectives: e.target.value })} className="mt-1.5 bg-card" rows={2} placeholder="What this program achieves." /></div>

            {editing && (
              <div>
                <Label>Status</Label>
                <div className="mt-1.5 flex gap-1.5">
                  {["draft", "active", "archived"].map((s) => (
                    <button key={s} onClick={() => setForm({ ...form, status: s })}
                      data-testid={`program-form-status-${s}`}
                      className={`flex items-center gap-1.5 text-xs font-medium rounded-full px-3 py-1.5 border transition-all capitalize ${
                        form.status === s ? "bg-primary text-primary-foreground border-primary" : "bg-card text-foreground/70 border-border hover:border-primary/40"
                      }`}>
                      {form.status === s && <Check className="w-3 h-3" />} {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="gap-2 sm:gap-2 flex-wrap">
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full">Cancel</Button>
            {!editing && (
              <Button variant="outline" onClick={() => save("draft")}
                data-testid="program-form-save-draft" className="rounded-full">
                <FileEdit className="w-4 h-4 mr-1.5" /> Save as Draft
              </Button>
            )}
            <Button onClick={() => save(editing ? form.status : "active")}
              data-testid={editing ? "program-form-save" : "program-form-save-active"}
              className="rounded-full">
              <Play className="w-4 h-4 mr-1.5" />
              {editing ? "Save changes" : "Save & Activate"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!confirmDel} onOpenChange={(v) => !v && setConfirmDel(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-destructive">Delete program?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-foreground/80 leading-relaxed">
            This permanently removes <strong>{confirmDel?.name}</strong> and every document, announcement, download, escalation and checklist attached to it. Users mapped to this program will lose access to it. This cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDel(null)} className="rounded-full">Cancel</Button>
            <Button
              className="rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => remove(confirmDel)} data-testid="confirm-delete-program">
              <Trash2 className="w-4 h-4 mr-1.5" /> Delete permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
